// Pure validation for the admin-editable autoscaler settings: the off-hours
// window and the concurrency ceiling.
//
// Kept out of the page so it can be unit tested on its own, and so the rules the
// backend enforces are written down in one place instead of inline in JSX.

/** Strict 24-hour HH:MM. The backend rejects anything else with a 422. */
export const HHMM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// Mirrors app/domain/admin/schemas.py on the core side. The two must not drift,
// so any change here needs the same change there.
export const MIN_CONCURRENCY = 0;
export const MAX_CONCURRENCY = 1000;
export const DEFAULT_CONCURRENCY = 1;

/**
 * Validate the window the admin typed in.
 *
 * An end earlier than the start is a normal overnight window -- prod runs
 * 21:15 -> 08:00 -- so only equality is rejected. Equal times are refused
 * because the autoscaler reads them as "always off hours" and would never bring
 * the voice stack back up.
 *
 * @returns {{ ok: true, error: null } | { ok: false, error: string }}
 */
export function validateOffHoursWindow(start, end) {
  const startText = (start || '').trim();
  const endText = (end || '').trim();

  if (!startText && !endText) {
    return { ok: false, error: 'Enter a start and an end time.' };
  }
  if (!startText) {
    return { ok: false, error: 'Enter a start time.' };
  }
  if (!endText) {
    return { ok: false, error: 'Enter an end time.' };
  }
  if (!HHMM_PATTERN.test(startText)) {
    return { ok: false, error: `"${startText}" is not a valid time. Use 24-hour HH:MM.` };
  }
  if (!HHMM_PATTERN.test(endText)) {
    return { ok: false, error: `"${endText}" is not a valid time. Use 24-hour HH:MM.` };
  }
  if (startText === endText) {
    return { ok: false, error: 'Start and end must be different times.' };
  }

  return { ok: true, error: null };
}

/** The nested PATCH body for PATCH /api/v1/admin/admin-settings. */
export function buildOffHoursPayload(start, end) {
  return {
    autoscaler: {
      off_hours: {
        start: (start || '').trim(),
        end: (end || '').trim(),
      },
    },
  };
}

/** The same PATCH with off_hours null, which clears the override server-side. */
export function buildOffHoursClearPayload() {
  return { autoscaler: { off_hours: null } };
}

/**
 * Validate the concurrency the admin typed in.
 *
 * The backend field is a strict int bounded 0..1000, so anything else is a 422
 * the operator would rather learn about before saving. Zero is allowed because
 * the API accepts it, but it is called out separately in the UI since it pins
 * the agent ceiling at zero.
 *
 * @returns {{ ok: true, error: null, value: number }
 *          | { ok: false, error: string, value: null }}
 */
export function validateConcurrency(input) {
  const text = String(input == null ? '' : input).trim();

  if (!text) {
    return { ok: false, error: 'Enter a concurrency value.', value: null };
  }
  // Digits only: parseInt would happily read "5abc" as 5.
  if (!/^\d+$/.test(text)) {
    return { ok: false, error: 'Enter a whole number, for example 5.', value: null };
  }

  const value = Number(text);
  if (value < MIN_CONCURRENCY || value > MAX_CONCURRENCY) {
    return {
      ok: false,
      error: `Must be between ${MIN_CONCURRENCY} and ${MAX_CONCURRENCY}.`,
      value: null,
    };
  }

  return { ok: true, error: null, value };
}

/** The nested PATCH body for a concurrency change. */
export function buildConcurrencyPayload(value) {
  return { autoscaler: { concurrency: value } };
}

/** The same PATCH with concurrency null, which clears the override server-side. */
export function buildConcurrencyClearPayload() {
  return { autoscaler: { concurrency: null } };
}

/**
 * Pull something readable out of an axios error. FastAPI returns a bare string
 * for HTTPException but a list of {msg} entries for a validation failure, so
 * both shapes are handled.
 */
export function extractApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const message = detail
      .map((item) => item?.msg)
      .filter(Boolean)
      .join('; ');
    if (message) {
      // Pydantic prefixes custom validators; the operator does not need it.
      return message.replace(/^Value error,\s*/i, '').trim();
    }
  }

  return fallback;
}
